/** 🔥 Hot Responses — v2-FIRST feature (Tom, 2026-07-16): one place for every
 * lead the Text Agent flagged HOT, filterable, forever — v1 only shows a 48h
 * badge on the card and a push notification that scrolls away.
 *
 * Data is all v1's own, already synced: LEAD_EDITS carries agentHot (epoch
 * ms), agentCat / agentCatReason (classification), agentReply (the AI's
 * suggested reply) — buyers-agent.js writes them, v2 reads them. The actual
 * inbound text comes from the comms thread matched to the lead. */
import { useMemo, useState } from "react";
import type { Lead } from "../../types";
import { natKey } from "../../lib/natkey";
import { newIdemKey } from "../../lib/idem";
import { statusOf, type EditsMap } from "../edits/api";
import type { Thread } from "../inbox/api";

export interface HotRow {
  lead: Lead;
  hotAt: number;
  cat: string | null;
  reason: string | null;
  reply: string | null;
  status: string;
}

/** Pure assembly so it's testable: every lead whose edits carry agentHot,
 * newest first. `windowMs` = null → all time. */
export function buildHotRows(leads: Lead[], edits: EditsMap, now: number, windowMs: number | null): HotRow[] {
  const out: HotRow[] = [];
  for (const lead of leads) {
    const f = edits.get(natKey(lead.address));
    const t = f?.["agentHot"];
    if (typeof t !== "number" || t <= 0) continue;
    if (windowMs !== null && now - t > windowMs) continue;
    const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
    out.push({
      lead,
      hotAt: t,
      cat: str(f?.["agentCat"]),
      reason: str(f?.["agentCatReason"]),
      reply: str(f?.["agentReply"]),
      status: statusOf(f),
    });
  }
  out.sort((a, b) => b.hotAt - a.hotAt);
  return out;
}

export const fmtAgo = (t: number, now: number): string => {
  const m = Math.max(0, Math.floor((now - t) / 60000));
  if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60);
  if (h < 48) return h + "h ago";
  return new Date(t).toLocaleDateString([], { month: "short", day: "numeric" });
};

const WINDOWS: { label: string; ms: number | null }[] = [
  { label: "Last 48h", ms: 172800000 },
  { label: "Last 7 days", ms: 7 * 86400000 },
  { label: "Last 30 days", ms: 30 * 86400000 },
  { label: "All time", ms: null },
];

export function HotPage({
  leads,
  edits,
  threads,
  findLeadAddress,
  onOpenLead,
  onOpenThread,
  onQuickReply,
  canText = false,
}: {
  leads: Lead[];
  edits: EditsMap;
  threads: Thread[];
  findLeadAddress: (t: Thread) => string | null;
  onOpenLead: (address: string) => void;
  onOpenThread: (peer: string) => void;
  /** send an SMS from right here (same live Quo door the Inbox uses) */
  onQuickReply?: (to: string, text: string, leadKey: string, name?: string, idemKey?: string) => Promise<void>;
  canText?: boolean;
}) {
  const [win, setWin] = useState(3); // default All time — it's the archive
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const now = Date.now();
  /* Tom 2026-08-07: "see the messages in the HOT section in a better way and
     quicker." The thread data is ALREADY here (the same threads the Inbox
     holds), so each card can expand into its last few messages and take a
     reply without leaving the page — zero fetches, one tap. */
  const [openThreadIds, setOpenThreadIds] = useState<Set<string>>(() => new Set());
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [sendState, setSendState] = useState<Record<string, "idle" | "sending" | "sent" | "error">>({});
  const idemRef = useMemo(() => new Map<string, string>(), []);
  const toggleThread = (id: string) =>
    setOpenThreadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const quickSend = async (rowId: string, t: Thread, leadAddr: string) => {
    const text = (draft[rowId] ?? "").trim();
    if (!text || !onQuickReply || sendState[rowId] === "sending") return;
    // one idem key per draft: a timeout retry re-sends the SAME key and the
    // server delivers exactly once (the Inbox composer's discipline)
    if (!idemRef.get(rowId)) idemRef.set(rowId, newIdemKey());
    setSendState((s) => ({ ...s, [rowId]: "sending" }));
    try {
      await onQuickReply(t.peer, text, natKey(leadAddr), t.name && t.name !== t.peer ? t.name : undefined, idemRef.get(rowId));
      idemRef.delete(rowId);
      setDraft((d) => ({ ...d, [rowId]: "" }));
      setSendState((s) => ({ ...s, [rowId]: "sent" }));
      setTimeout(() => setSendState((s) => ({ ...s, [rowId]: "idle" })), 2500);
    } catch {
      setSendState((s) => ({ ...s, [rowId]: "error" }));
    }
  };

  // address (natKey) → thread, via the same matcher the Inbox uses
  const threadByAddr = useMemo(() => {
    const m = new Map<string, Thread>();
    for (const t of threads) {
      const addr = findLeadAddress(t);
      if (addr && !m.has(natKey(addr))) m.set(natKey(addr), t);
    }
    return m;
  }, [threads, findLeadAddress]);

  const all = useMemo(() => buildHotRows(leads, edits, now, WINDOWS[win]?.ms ?? null), [leads, edits, now, win]);

  const sources = useMemo(() => ["all", ...new Set(all.map((r) => r.lead.source))], [all]);
  const statuses = useMemo(() => ["all", ...new Set(all.map((r) => r.status))], [all]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((r) => {
      if (source !== "all" && r.lead.source !== source) return false;
      if (status !== "all" && r.status !== status) return false;
      if (!needle) return true;
      const t = threadByAddr.get(natKey(r.lead.address));
      const lastIn = t ? [...t.messages].reverse().find((m) => m.direction === "in")?.body ?? "" : "";
      return [r.lead.address, r.lead.seller ?? "", r.reason ?? "", r.reply ?? "", lastIn]
        .some((v) => v.toLowerCase().includes(needle));
    });
  }, [all, source, status, q, threadByAddr]);

  return (
    <div className="hotpage">
      <header className="bar">
        <input placeholder="Search address, seller, or message…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={win} onChange={(e) => setWin(Number(e.target.value))}>
          {WINDOWS.map((w, i) => (
            <option key={w.label} value={i}>{w.label}</option>
          ))}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          {sources.map((s) => (
            <option key={s} value={s}>{s === "all" ? "All sources" : s}</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {statuses.map((s) => (
            <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>
          ))}
        </select>
        <span className="count">{rows.length} hot {rows.length === 1 ? "reply" : "replies"}</span>
      </header>

      {rows.length === 0 ? (
        <div className="empty gate">
          <strong>No hot replies {WINDOWS[win]?.ms !== null ? "in this window" : "yet"}.</strong>
          <span>
            When the Text Agent flags a reply 🔥 HOT (offer talk, meeting requests, "do you have
            an agent?"), it lands here — permanently, not just as a 48-hour badge.
          </span>
        </div>
      ) : (
        <div className="hotlist">
          {rows.map((r) => {
            const t = threadByAddr.get(natKey(r.lead.address)) ?? null;
            const lastIn = t ? [...t.messages].reverse().find((m) => m.direction === "in") : null;
            return (
              <div key={r.lead.id} className="hotcard">
                <div className="hothead">
                  <span className="lbadge bhot">🔥</span>
                  <button className="hotaddr" onClick={() => onOpenLead(r.lead.address)}>{r.lead.address}</button>
                  <span className="sub">{fmtAgo(r.hotAt, now)}</span>
                </div>
                <div className="hotmeta">
                  <span className="lbadge boff">{r.lead.source}</span>
                  <span className={"statuschip s-" + r.status.toLowerCase().replace(/[^a-z]+/g, "-")}>{r.status}</span>
                  {r.cat ? <span className="lbadge bdup">{r.cat.replace(/_/g, " ")}</span> : null}
                  {r.lead.seller ? <span className="sub">{r.lead.seller}</span> : null}
                </div>
                {lastIn?.body ? <blockquote className="hotquote">“{lastIn.body.slice(0, 220)}”</blockquote> : null}
                {r.reason ? <div className="sub">Why: {r.reason}</div> : null}
                {r.reply ? <div className="hotreply">AI suggested: {r.reply.slice(0, 180)}</div> : null}
                {t && openThreadIds.has(r.lead.id) ? (
                  <div className="hotthread">
                    {t.messages.slice(-6).map((m) => (
                      <div key={m.id} className={"hotmsg " + m.direction}>
                        <span className="hotmsgbody">{m.channel === "call" ? "📞 Call" : m.body}</span>
                        <span className="hotmsgat">{fmtAgo(m.createdAt, now)}</span>
                      </div>
                    ))}
                    <div className="hotcompose">
                      <input
                        placeholder={canText ? "Reply…" : "Sending is off on this beta"}
                        value={draft[r.lead.id] ?? ""}
                        disabled={!canText || sendState[r.lead.id] === "sending"}
                        onChange={(e) => setDraft((d) => ({ ...d, [r.lead.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") void quickSend(r.lead.id, t, r.lead.address); }}
                      />
                      <button
                        className="btn slim primary"
                        disabled={!canText || !(draft[r.lead.id] ?? "").trim() || sendState[r.lead.id] === "sending"}
                        onClick={() => void quickSend(r.lead.id, t, r.lead.address)}
                      >
                        {sendState[r.lead.id] === "sending" ? "…" : sendState[r.lead.id] === "sent" ? "Sent ✓" : "Send"}
                      </button>
                    </div>
                    {sendState[r.lead.id] === "error" ? <div className="sub hotsenderr">Send failed — nothing went out. Try again.</div> : null}
                  </div>
                ) : null}
                <div className="hotactions">
                  {t ? (
                    <button className="btn slim primary" onClick={() => toggleThread(r.lead.id)}>
                      {openThreadIds.has(r.lead.id) ? "Hide messages" : "💬 Messages (" + t.messages.length + ")"}
                    </button>
                  ) : null}
                  <button className="btn slim" onClick={() => onOpenLead(r.lead.address)}>Open lead</button>
                  {t ? <button className="vtab" onClick={() => onOpenThread(t.peer)}>Full thread →</button> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
